"use client";

import { Fragment, useState, useEffect, use, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";

import { Dialog, Transition } from "@headlessui/react";
import { usePathname, useRouter, Image, Link, dynamic, useNavigate, useSearchParams } from "@/src/lib/next-compat";
import { getCanonicalLessonApiV1LessonsLessonIdCanonicalGet } from "@/src/api/sdk.gen";
import { apiFetch } from "@/src/lib/api-fetch";
import { formatApiErrorMessage } from "@/src/lib/error-utils";
import { useCourseMetadata } from "./hooks/useCourseMetadata";
import {
    ArrowLeft,
    ArrowRightIcon,
    Building2,
    Check,
    Pencil,
    FileText,
    Globe,
    Image as ImageLucide,
    Loader2,
    Lock,
    SparklesIcon,
    Trash2,
    Upload,
    X,
    DollarSign,
    Languages,
} from "lucide-react";
import { UploadLessonModal } from "@/components/admin/UploadLessonModal";
import ResourceList, { type Resource } from "@/components/admin/ResourceList";
import type { ResourceFormData } from "@/lib/types/resources";
import LessonsTab from "@/components/admin/course-details/LessonsTab";
import PlacementTab from "@/components/admin/course-details/PlacementTab";
import MetadataTab from "@/components/admin/course-details/MetadataTab";
import PromptsTab from "@/components/admin/course-details/PromptsTab";
import PlansTab from "@/components/admin/course-details/PlansTab";
import TranslationsTab, { type TranslationStatus, type TranslationScope } from "@/components/admin/course-details/TranslationsTab";
import { useLessonFilters } from "./hooks/useLessonFilters";
import { useCanonicalStatus } from "./hooks/useCanonicalStatus";
import {
    getLessonQuizBadge,
    getLessonStatus,
    getStatusParams,
    isBlockOnlyCourse,
    courseHasStaticPlacement,
    getPlacementQuizGenerateDisabledReason,
} from "@/lib/admin/course-details";
import type { CourseDetails, Lesson, Module, Level } from "@/lib/admin/course-details";
import type { ToastState } from "@/components/admin/review/types";

const MenuDropdown = dynamic(
    () => import("@/components/ui/MenuDropdown").then((m) => m.MenuDropdown),
    { ssr: false },
);
import { TaskProgressModal } from "@/components/ui/TaskProgressModal";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

async function fetchLessonCanonicalStatus(lessonId: number): Promise<boolean | null> {
    try {
        const { data, error } = await getCanonicalLessonApiV1LessonsLessonIdCanonicalGet({
            path: { lesson_id: lessonId },
        });
        if (error || !data) return null;
        const resData = data as unknown as { has_canonical_structured?: boolean };
        return resData.has_canonical_structured === true;
    } catch {
        return null;
    }
}


export default function CourseDetailsPage({ params }: { params?: Promise<{ id: string }> } = {}) {
    const routeParams = useParams<{ id: string }>();
    const id = routeParams.id || "";
    const navigate = useNavigate();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const [course, setCourse] = useState<CourseDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

    // Task Progress State
    const [taskModal, setTaskModal] = useState<{
        isOpen: boolean;
        status: "idle" | "processing" | "completed" | "failed";
        error: string | null;
        title?: string;
        description?: string;
    }>({
        isOpen: false,
        status: "idle",
        error: null
    });

    const [expandedModules, setExpandedModules] = useState<Record<number, boolean>>({});
    const [generatingCanonicalLessonId, setGeneratingCanonicalLessonId] = useState<number | null>(null);
    const [canonicalGeneratingByLessonId, setCanonicalGeneratingByLessonId] = useState<Record<number, boolean>>({});
    const [isCanonicalFanoutSubmitting, setIsCanonicalFanoutSubmitting] = useState(false);
    const [activeView, setActiveView] = useState<
        "lessons" | "placement" | "prompts" | "metadata" | "resources" | "pricing" | "translations"
    >("lessons");
    const [translationStatuses, setTranslationStatuses] = useState<TranslationStatus[] | null>(null);
    const [loadingTranslations, setLoadingTranslations] = useState(false);
    const [translationsLoadError, setTranslationsLoadError] = useState<string | null>(null);
    const [translationBusy, setTranslationBusy] = useState(false);
    const [resources, setResources] = useState<Resource[]>([]);
    const [loadingResources, setLoadingResources] = useState(false);
    const routerReplace = useCallback((next: string) => {
        navigate(next, { replace: true });
    }, []);
    const {
        lessonApprovalFilter,
        setLessonApprovalFilter,
        lessonQuizFilter,
        setLessonQuizFilter,
        lessonCanonicalFilter,
        setLessonCanonicalFilter,
        lessonTypeFilter,
        setLessonTypeFilter,
        hasActiveLessonFilters,
        resetLessonFilters,
    } = useLessonFilters({
        pathname,
        routerReplace,
        searchParams,
    });

    const [coverUploading, setCoverUploading] = useState(false);
    const [coverError, setCoverError] = useState<string | null>(null);
    const [isCoverDialogOpen, setIsCoverDialogOpen] = useState(false);
    const [coverUrlDraft, setCoverUrlDraft] = useState("");
    const [isCourseDetailsDialogOpen, setIsCourseDetailsDialogOpen] = useState(false);
    const [courseTitleDraft, setCourseTitleDraft] = useState("");
    const [courseDescriptionDraft, setCourseDescriptionDraft] = useState("");
    const [courseSchemaVersionDraft, setCourseSchemaVersionDraft] = useState<"engineering" | "professional">("engineering");
    const [courseOrgDraft, setCourseOrgDraft] = useState<number | null>(null);
    const [courseIsPublicDraft, setCourseIsPublicDraft] = useState(true);
    const [courseOrgsDraft, setCourseOrgsDraft] = useState<number[]>([]);
    const [organizations, setOrganizations] = useState<{ id: number; name: string }[]>([]);
    const [orgsLoadError, setOrgsLoadError] = useState(false);
    const [courseDetailsSaving, setCourseDetailsSaving] = useState(false);
    const [courseDetailsError, setCourseDetailsError] = useState<string | null>(null);
    const [accessModeUpdating, setAccessModeUpdating] = useState(false);
    const [deleteInProgress, setDeleteInProgress] = useState(false);
    const [placementReloadNonce, setPlacementReloadNonce] = useState(0);
    const [preferencePromptSaving, setPreferencePromptSaving] = useState(false);
    const [toast, setToast] = useState<ToastState>(null);
    const coverInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!toast) return;
        const timer = setTimeout(() => setToast(null), 4000);
        return () => clearTimeout(timer);
    }, [toast]);

    const showToast = useCallback((message: string, type: "success" | "error" = "success") => {
        setToast({ message, type });
    }, []);

    const handleToggleRequireUserFutureGoal = useCallback(
        async (next: boolean) => {
            if (!course) return;
            setPreferencePromptSaving(true);
            try {
                const res = await apiFetch(`/api/admin/courses/${course.id}`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ require_user_future_goal: next }),
                });
                if (!res.ok) {
                    const err = (await res.json().catch(() => ({}))) as {
                        error?: string;
                        details?: string;
                    };
                    const msg = [err.error, err.details].filter(Boolean).join("\n\n");
                    showToast(msg || "Failed to update student preference prompt", "error");
                    return;
                }
                const updated = (await res.json()) as { require_user_future_goal?: boolean };
                setCourse((c) =>
                    c
                        ? { ...c, require_user_future_goal: updated.require_user_future_goal ?? next }
                        : null,
                );
            } catch {
                showToast("Failed to update student preference prompt", "error");
            } finally {
                setPreferencePromptSaving(false);
            }
        },
        [course, showToast],
    );

    const toggleModule = (moduleId: number) => {
        setExpandedModules(prev => ({
            ...prev,
            [moduleId]: !prev[moduleId]
        }));
    };

    const fetchCourse = useCallback(async () => {
        try {
            // Add timestamp to prevent caching
            const response = await apiFetch(`/api/admin/courses/${id}?t=${Date.now()}`);
            if (response.ok) {
                const data = await response.json();
                setCourse(data);
                return data as CourseDetails;
            }
        } catch (error) {
            console.error("Failed to fetch course:", error);
        } finally {
            setLoading(false);
        }
        return null;
    }, [id]);

    useEffect(() => {
        fetchCourse();
    }, [fetchCourse]);

    useEffect(() => {
        if (searchParams.get("view") === "placement") {
            setActiveView("placement");
        }
    }, [searchParams]);

    const openPlacementTab = useCallback(() => {
        setActiveView("placement");
        const params = new URLSearchParams(searchParams.toString());
        params.set("view", "placement");
        routerReplace(`${pathname}?${params.toString()}`);
    }, [pathname, routerReplace, searchParams]);

    const fetchResources = useCallback(async () => {
        setLoadingResources(true);
        try {
            const response = await apiFetch(`/api/admin/resources?course_id=${course?.id ?? id}`);
            if (response.ok) {
                const data = await response.json();
                setResources(data);
            }
        } catch (error) {
            console.error("Failed to fetch resources:", error);
        } finally {
            setLoadingResources(false);
        }
    }, [course?.id, id]);

    useEffect(() => {
        if (activeView === "resources" && course?.id) {
            fetchResources();
        }
    }, [activeView, course?.id, fetchResources]);

    const fetchTranslationStatus = useCallback(async () => {
        if (!course?.id) return;
        setLoadingTranslations(true);
        try {
            const response = await apiFetch(`/api/v1/admin/courses/${course.id}/translations`);
            if (response.ok) {
                const data = await response.json();
                setTranslationStatuses(data.languages || []);
                setTranslationsLoadError(null);
            } else {
                const errData = await response.json().catch(() => ({}));
                setTranslationsLoadError(errData.detail || "Failed to load translations");
            }
        } catch (err) {
            setTranslationsLoadError(err instanceof Error ? err.message : "Failed to load translations");
        } finally {
            setLoadingTranslations(false);
        }
    }, [course?.id]);

    useEffect(() => {
        if (activeView === "translations" && course?.id) {
            fetchTranslationStatus();
        }
    }, [activeView, course?.id, fetchTranslationStatus]);

    const handleTranslate = async (
        language: string,
        scope: TranslationScope,
        opts?: { lessonId?: number; includeStale?: boolean }
    ) => {
        if (!course?.id) return;
        setTranslationBusy(true);
        try {
            const response = await apiFetch(`/api/v1/admin/courses/${course.id}/translations`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    language,
                    scope,
                    lessonId: opts?.lessonId,
                    includeStale: opts?.includeStale ?? false,
                }),
            });
            if (response.ok) {
                const res = await response.json();
                showToast(
                    `Translation queued (${res.lessonsQueued ?? 0} lessons, ${res.quizzesQueued ?? 0} quizzes, ${res.placementQueued ?? 0} placement levels)`,
                    "success"
                );
                await fetchTranslationStatus();
            } else {
                const errData = await response.json().catch(() => ({}));
                showToast(String(errData.detail || "Failed to dispatch translation"), "error");
            }
        } catch (err: any) {
            showToast(String(err?.message || "Failed to dispatch translation"), "error");
        } finally {
            setTranslationBusy(false);
        }
    };

    const handleSetOffered = async (language: string, offered: boolean) => {
        if (!course?.id) return;
        setTranslationBusy(true);
        try {
            const response = await apiFetch(`/api/v1/admin/courses/${course.id}/translations/availability`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ language, offered }),
            });
            if (response.ok) {
                showToast(
                    offered ? `${language} offered to learners` : `${language} removed from offer`,
                    "success"
                );
                await fetchTranslationStatus();
            } else {
                const errData = await response.json().catch(() => ({}));
                const detail = errData.detail;
                const blockingMsg = detail?.blocking ? detail.blocking.join(". ") : detail?.message || detail || "Failed to update availability";
                showToast(String(blockingMsg), "error");
            }
        } catch (err: any) {
            showToast(String(err?.message || "Failed to update language availability"), "error");
        } finally {
            setTranslationBusy(false);
        }
    };

    const handleSyncStale = async (language: string) => {
        await handleTranslate(language, "lessons", { includeStale: true });
    };

    const handleResourceCreated = async (data: ResourceFormData | FormData) => {
        const isFormData = data instanceof FormData;
        const fetchOptions: RequestInit = {
            method: "POST",
        };

        if (isFormData) {
            const formData = data as FormData;
            if (!formData.get("course_id")) {
                formData.append("course_id", String(course?.id));
            }
            fetchOptions.body = formData;
        } else {
            // JSON payload
            fetchOptions.headers = { "Content-Type": "application/json" };
            fetchOptions.body = JSON.stringify({
                ...data,
                course_id: course?.id,
                resource_type: (data as any).resource_type || (data as any).type,
            });
        }

        const response = await apiFetch("/api/admin/resources", fetchOptions);
        if (!response.ok) {
            let errorMsg = "Failed to create resource";
            try {
                const errData = await response.json();
                if (errData?.detail) errorMsg = errData.detail;
            } catch {}
            throw new Error(errorMsg);
        }
        await fetchResources();
    };

    const handleResourceUpdated = async (resourceId: number, data: Partial<ResourceFormData> | FormData) => {
        const isFormData = data instanceof FormData;
        const fetchOptions: RequestInit = { method: "PATCH" };

        if (isFormData) {
            fetchOptions.body = data;
        } else {
            fetchOptions.headers = { "Content-Type": "application/json" };
            fetchOptions.body = JSON.stringify({
                ...data,
                resource_type: (data as any).resource_type || (data as any).type,
            });
        }

        const response = await apiFetch(`/api/admin/resources/${resourceId}`, fetchOptions);
        if (!response.ok) {
            let errorMsg = "Failed to update resource";
            try {
                const errData = await response.json();
                if (errData?.detail) errorMsg = errData.detail;
            } catch {}
            throw new Error(errorMsg);
        }
        await fetchResources();
    };

    const handleResourceDeleted = async (resourceId: number) => {
        const response = await apiFetch(`/api/admin/resources/${resourceId}`, { method: "DELETE" });
        if (!response.ok) throw new Error("Failed to delete resource");
        await fetchResources();
    };
    useEffect(() => {
        apiFetch("/api/admin/organizations")
            .then((r) => {
                if (!r.ok) throw new Error("Failed to load organizations");
                return r.json();
            })
            .then((data) => { if (Array.isArray(data)) setOrganizations(data); })
            .catch(() => setOrgsLoadError(true));
    }, []);

    const {
        canonicalStatus,
        setIsPollingCanonicalStatus,
        fetchCanonicalStatus,
    } = useCanonicalStatus({
        id,
        courseId: course?.id ?? null,
    });
    const {
        domains,
        goals,
        departments,
        newDomainLabel,
        setNewDomainLabel,
        newGoalLabel,
        setNewGoalLabel,
        newDepartmentLabel,
        setNewDepartmentLabel,
        editingId,
        setEditingId,
        editingLabel,
        setEditingLabel,
        metadataBusyKey,
        handleAddMetadata,
        handleSaveMetadataLabel,
        handleDeleteMetadata,
    } = useCourseMetadata({ id });

    // Refetch when page becomes visible (e.g., after navigating back from review)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                fetchCourse();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [fetchCourse]);

    const handleViewLesson = async (lessonId: number, genLessonUuid: string | undefined, status: string) => {
        if (status === "not_started") return;

        if (genLessonUuid) {
            navigate(`/admin/reviews/lesson/${genLessonUuid}`);
            return;
        }

        if (status === "uploaded") {
            setTaskModal({
                isOpen: true,
                status: "failed",
                error: "Generate canonical lesson first. Viewing should not create generated lessons automatically.",
                title: "Canonical generation required",
            });
        }
    };

    const handleGenerateCanonical = async (lessonId: number, lessonTitle: string) => {
        setTaskModal({
            isOpen: true,
            status: "processing",
            error: null,
            title: "Generating canonical lesson…",
            description: `Queueing canonical generation for “${lessonTitle}”. This runs in the background.`,
        });
        setGeneratingCanonicalLessonId(lessonId);
        setCanonicalGeneratingByLessonId((prev) => ({ ...prev, [lessonId]: true }));
        try {
            const res = await apiFetch(`/api/admin/lessons/${lessonId}/generate-canonical`, {
                method: "POST",
            });
            const data = (await res.json().catch(() => ({}))) as Record<string, any>;
            if (!res.ok) {
                throw new Error(formatApiErrorMessage(data, "Failed to start generation"));
            }
            setTaskModal({
                isOpen: true,
                status: "completed",
                error: null,
                title: "Canonical generation started",
                description:
                    "The task has been queued. Refresh this page in a moment to see lesson status.",
            });
            // Keep refreshing until canonical appears (or timeout).
            const startedAt = Date.now();
            const poll = async () => {
                const hasCanonicalStructured = await fetchLessonCanonicalStatus(lessonId);
                if (hasCanonicalStructured) {
                    await fetchCourse();
                    setCanonicalGeneratingByLessonId((prev) => ({ ...prev, [lessonId]: false }));
                    return;
                }
                if (Date.now() - startedAt > 60_000) {
                    setCanonicalGeneratingByLessonId((prev) => ({ ...prev, [lessonId]: false }));
                    return;
                }
                setTimeout(poll, 2500);
            };
            setTimeout(poll, 2500);
        } catch (e) {
            setTaskModal({
                isOpen: true,
                status: "failed",
                error: e instanceof Error ? e.message : "Request failed",
                title: "Could not start canonical generation",
                description: undefined,
            });
            setCanonicalGeneratingByLessonId((prev) => ({ ...prev, [lessonId]: false }));
        } finally {
            setGeneratingCanonicalLessonId(null);
        }
    };

    const handleApproveAll = async () => {
        if (!confirm("Are you sure you want to approve ALL lessons in this course? This will trigger quiz generation for every lesson.")) {
            return;
        }

        setTaskModal({
            isOpen: true,
            status: "processing",
            error: null,
            // Add custom text for this action
            title: "Approving Lessons...",
            description: "We are approving all lessons and generating their quizzes. This may take a moment."
        });

        try {
            const res = await apiFetch(`/api/admin/courses/${course!.id}/approve-all-lessons`, {
                method: "POST"
            });

            if (!res.ok) throw new Error("Failed to approve lessons");

            const result = await res.json();

            // Wait a moment so user sees the processing
            setTimeout(() => {
                setTaskModal({ isOpen: true, status: "completed", error: null });
                fetchCourse();
            }, 1000);

        } catch (error) {
            setTaskModal({
                isOpen: true,
                status: "failed",
                error: error instanceof Error ? error.message : "Unknown error"
            });
        }
    };

    const handleGenerateQuiz = async () => {
        if (!course) return;
        if (!canonicalStatus?.is_complete) {
            setTaskModal({
                isOpen: true,
                status: "failed",
                error: "Generate canonical lessons first. Placement quiz is available only after canonical generation completes.",
                title: "Canonical generation required",
            });
            return;
        }

        // Open modal in processing state immediately
        setTaskModal({ isOpen: true, status: "processing", error: null });

        try {
            const response = await apiFetch(`/api/admin/courses/${course.id}/generate-placement-quiz`, {
                method: "POST"
            });

            if (response.ok) {
                // Poll for completion
                const pollInterval = 2000; // 2 seconds
                const maxAttempts = 30; // 1 minute timeout
                let attempts = 0;

                const checkStatus = async () => {
                    attempts++;
                    try {
                        const statusRes = await apiFetch(`/api/admin/courses/${course.id}/placement-quiz-status`);
                        if (statusRes.ok) {
                            const statusData = await statusRes.json();
                            if (statusData.generated) {
                                setTaskModal(prev => ({ ...prev, status: "completed" }));
                                fetchCourse(); // Refresh data in background
                                setPlacementReloadNonce((n) => n + 1);
                                return;
                            }
                        }
                    } catch (e) {
                        console.error("Polling error", e);
                    }

                    if (attempts < maxAttempts) {
                        setTimeout(checkStatus, pollInterval);
                    } else {
                        setTaskModal(prev => ({
                            ...prev,
                            status: "failed",
                            error: "Generation timed out. Please check back later."
                        }));
                    }
                };

                // Start polling
                checkStatus();

            } else {
                const error = await response.json();
                setTaskModal(prev => ({
                    ...prev,
                    status: "failed",
                    error: error.error || "Failed to start generation"
                }));
            }
        } catch (error) {
            console.error("Failed to generate quiz:", error);
            setTaskModal(prev => ({
                ...prev,
                status: "failed",
                error: "An error occurred while starting quiz generation."
            }));
        }
    };

    const handleUpdateStatus = async (newStatus: string) => {
        if (!course) return;

        try {
            const response = await apiFetch(`/api/admin/courses/${course.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ status: newStatus })
            });

            if (response.ok) {
                setCourse(prev => prev ? { ...prev, status: newStatus } : null);
            } else {
                const error = await response.json();
                showToast(error.error || "Failed to update status", "error");
            }
        } catch (error) {
            console.error("Failed to update status:", error);
            showToast("An error occurred while updating status.", "error");
        }
    };

    const handleSetAccessMode = async (accessMode: "SPONSORED" | "INDIVIDUAL") => {
        if (!course || accessModeUpdating) return;

        if (!course.course_organizations?.length) {
            showToast("Assign an organization in Edit Course Details before setting access mode.", "error");
            return;
        }

        setAccessModeUpdating(true);
        try {
            const response = await apiFetch(`/api/admin/courses/${course.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ access_mode: accessMode }),
            });
            const data = (await response.json().catch(() => ({}))) as {
                error?: string;
                course_organizations?: CourseDetails["course_organizations"];
            };
            if (!response.ok) {
                showToast(data.error || "Failed to update access mode", "error");
                return;
            }
            setCourse((prev) =>
                prev
                    ? {
                          ...prev,
                          course_organizations: data.course_organizations ?? prev.course_organizations,
                      }
                    : prev,
            );
            showToast(
                accessMode === "SPONSORED"
                    ? "Course is now sponsored for organization members."
                    : "Course is now individual (purchase per level).",
            );
        } catch {
            showToast("Network error while updating access mode.", "error");
        } finally {
            setAccessModeUpdating(false);
        }
    };

    const handleDeleteCourse = async () => {
        if (!course || deleteInProgress) return;

        const confirmed = window.confirm(
            `Delete "${course.title}"?\n\nIf this course has payment history, it will be retired and its plans deactivated instead of removed. Otherwise all modules, lessons, quizzes, placement data, and student progress are permanently deleted.`,
        );
        if (!confirmed) return;

        setDeleteInProgress(true);
        try {
            const response = await apiFetch(`/api/admin/courses/${course.id}`, { method: "DELETE" });
            const data = (await response.json().catch(() => ({}))) as {
                error?: string;
                details?: string;
                message?: string;
                archived?: boolean;
            };
            if (!response.ok) {
                const msg = [data.error, data.details].filter(Boolean).join("\n\n");
                showToast(msg || "Failed to delete course", "error");
                return;
            }
            if (data.archived) {
                showToast(
                    data.message ||
                        "Course has payment history and was retired instead of deleted.",
                    "success",
                );
            }
            navigate("/admin/courses");
        } catch {
            showToast("Network error while deleting course.", "error");
        } finally {
            setDeleteInProgress(false);
        }
    };

    const handleCoverImageSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !course) {
            return;
        }
        setCoverUploading(true);
        setCoverError(null);
        const formData = new FormData();
        formData.append("file", file);
        try {
            const response = await apiFetch(`/api/admin/courses/${id}/cover-image`, {
                method: "POST",
                body: formData,
            });
            const data = (await response.json().catch(() => ({}))) as { error?: string; cover_image_url?: string };
            if (!response.ok) {
                setCoverError(typeof data.error === "string" ? data.error : "Upload failed");
                return;
            }
            if (data.cover_image_url) {
                setCourse((prev) =>
                    prev ? { ...prev, cover_image_url: data.cover_image_url as string } : null,
                );
            }
        } catch {
            setCoverError("Network error while uploading");
        } finally {
            setCoverUploading(false);
            e.target.value = "";
        }
    };

    const handleRemoveCoverImage = async () => {
        if (!course) {
            return;
        }
        setCoverUploading(true);
        setCoverError(null);
        try {
            const response = await apiFetch(`/api/admin/courses/${id}/cover-image`, {
                method: "DELETE",
            });
            if (!response.ok) {
                const data = (await response.json().catch(() => ({}))) as { error?: string };
                setCoverError(typeof data.error === "string" ? data.error : "Failed to remove image");
                return;
            }
            setCourse((prev) => (prev ? { ...prev, cover_image_url: null } : null));
        } catch {
            setCoverError("Network error while removing image");
        } finally {
            setCoverUploading(false);
        }
    };

    const handleSaveCoverImageUrl = async () => {
        if (!course) return;
        const url = coverUrlDraft.trim();
        if (!url) {
            setCoverError("Please paste an image URL.");
            return;
        }
        setCoverUploading(true);
        setCoverError(null);
        try {
            const response = await apiFetch(`/api/admin/courses/${id}/cover-image`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ cover_image_url: url }),
            });
            const data = (await response.json().catch(() => ({}))) as { error?: string; cover_image_url?: string };
            if (!response.ok) {
                setCoverError(typeof data.error === "string" ? data.error : "Failed to save image URL");
                return;
            }
            if (data.cover_image_url) {
                setCourse((prev) =>
                    prev ? { ...prev, cover_image_url: data.cover_image_url as string } : null,
                );
                setCoverUrlDraft(data.cover_image_url);
            }
        } catch {
            setCoverError("Network error while saving image URL");
        } finally {
            setCoverUploading(false);
        }
    };

    const openCourseDetailsDialog = () => {
        setCourseTitleDraft(course?.title ?? "");
        setCourseDescriptionDraft(course?.description ?? "");
        setCourseSchemaVersionDraft((course?.schema_version as "engineering" | "professional") ?? "engineering");
        setCourseIsPublicDraft(course?.is_public !== false);
        const currentOrgIds = (course?.course_organizations || []).map((o) => o.organization_id);
        setCourseOrgsDraft(currentOrgIds);
        setCourseOrgDraft(currentOrgIds[0] ?? null);
        setCourseDetailsError(null);
        setIsCourseDetailsDialogOpen(true);
    };

    const handleSaveCourseDetails = async () => {
        if (!course) return;
        const title = courseTitleDraft.trim();
        if (!title) {
            setCourseDetailsError("Course name is required.");
            return;
        }
        if (orgsLoadError) {
            setCourseDetailsError("Organizations failed to load. Please close this dialog and try again.");
            return;
        }
        if (!courseIsPublicDraft && courseOrgsDraft.length === 0) {
            setCourseDetailsError("At least one organization is required for a restricted course.");
            return;
        }
        setCourseDetailsSaving(true);
        setCourseDetailsError(null);
        try {
            const response = await apiFetch(`/api/admin/courses/${course.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    title,
                    description: courseDescriptionDraft.trim() ? courseDescriptionDraft.trim() : null,
                    schema_version: courseSchemaVersionDraft,
                    is_public: courseIsPublicDraft,
                    organization_ids: courseOrgsDraft,
                }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                setCourseDetailsError(data?.error || data?.detail || "Failed to update course details.");
                return;
            }
            setCourse((prev) =>
                prev
                    ? {
                          ...prev,
                          title: data.title ?? title,
                          description: data.description ?? null,
                          schema_version: data.schema_version ?? courseSchemaVersionDraft,
                          is_public: data.is_public ?? courseIsPublicDraft,
                          course_organizations: data.course_organizations ?? prev.course_organizations,
                      }
                    : prev
            );
            setIsCourseDetailsDialogOpen(false);
            showToast("Course details updated successfully.", "success");
        } catch {
            setCourseDetailsError("Network error while saving course details.");
        } finally {
            setCourseDetailsSaving(false);
        }
    };

    if (loading) {
        return <div className="animate-pulse space-y-4">
            <div className="h-8 w-1/3 bg-slate-200 rounded"></div>
            <div className="h-64 bg-slate-100 rounded-2xl"></div>
        </div>;
    }

    if (!course) {
        return <div>Course not found</div>;
    }

    const canonicalProgressTotal = canonicalStatus?.total_lessons ?? 0;
    const canonicalProgressGenerated = canonicalStatus?.generated ?? 0;
    const canonicalProgressPercent = canonicalProgressTotal > 0
        ? Math.round((canonicalProgressGenerated / canonicalProgressTotal) * 100)
        : 0;

    const courseAccessMode = course.course_organizations?.[0]?.access_mode ?? "INDIVIDUAL";
    const isCourseSponsored = courseAccessMode === "SPONSORED";
    const hasCourseOrganization = (course.course_organizations?.length ?? 0) > 0;

    const handleGenerateCanonicalForCourse = async (force: boolean) => {
        if (!course || isCanonicalFanoutSubmitting) return;

        setIsCanonicalFanoutSubmitting(true);
        setTaskModal({
            isOpen: true,
            status: "processing",
            error: null,
            title: "Starting canonical generation",
            description: "Queueing canonical lesson generation tasks for this course.",
        });

        try {
            const res = await apiFetch(`/api/courses/${course.id}/canonical-lessons/generate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ force }),
            });
            const data = (await res.json().catch(() => ({}))) as Record<string, any>;
            if (!res.ok) {
                throw new Error(formatApiErrorMessage(data, "Failed to start canonical generation"));
            }

            setTaskModal({
                isOpen: true,
                status: "completed",
                error: null,
                title: "Canonical generation queued",
                description: `Queued ${data.enqueued_lessons ?? 0} lessons (${data.skipped_existing ?? 0} already existed).`,
            });
            setIsPollingCanonicalStatus(true);
            fetchCanonicalStatus();
            fetchCourse();
        } catch (err) {
            setTaskModal({
                isOpen: true,
                status: "failed",
                error: formatApiErrorMessage(err, "Failed to start canonical generation"),
                title: "Could not start canonical generation",
            });
        } finally {
            setIsCanonicalFanoutSubmitting(false);
        }
    };

    const modules = course.modules ?? [];
    const placementQuizzes = course.placement_quizzes ?? [];
    const blockOnlyCourse = isBlockOnlyCourse(course);
    const hasStaticPlacement = courseHasStaticPlacement(placementQuizzes);
    const placementQuizGenerateDisabledReason = getPlacementQuizGenerateDisabledReason({
        canonicalGenerationComplete: Boolean(canonicalStatus?.is_complete),
        blockOnlyCourse,
        hasStaticPlacement,
    });

    const totalModules = modules.length;
    const totalLessons = modules.reduce((acc, m) => acc + m.lessons.length, 0);
    const lessonTypeOptions = Array.from(
        new Set(
            modules
                .flatMap((m) => m.lessons.map((l) => (l.lesson_type || "").trim()))
                .filter((t) => t.length > 0),
        ),
    ).sort((a, b) => a.localeCompare(b));

    // Group modules by level
    const modulesByLevel = modules.reduce((acc, module) => {
        const levelId = module.levels?.id || -1; // -1 for "General" / No Level
        if (!acc[levelId]) {
            acc[levelId] = {
                level: module.levels,
                modules: []
            };
        }
        acc[levelId].modules.push(module);
        return acc;
    }, {} as Record<number, { level: Level | null, modules: Module[] }>);

    const filteredModulesByLevel = (() => {
        const lessonMatchesFilters = (lesson: Lesson) => {
            const status = getLessonStatus(lesson);
            const raw = lesson.generated_lessons;
            const gl = Array.isArray(raw) ? raw[0] : raw ?? null;
            const hasQuiz = Boolean(gl?.quiz_generated);
            const hasCanonical = Boolean(gl?.structured_content);

            const matchesApproval =
                lessonApprovalFilter === "all" ||
                (lessonApprovalFilter === "approved" && status === "APPROVED") ||
                (lessonApprovalFilter === "unapproved" && status !== "APPROVED");

            const matchesQuiz =
                lessonQuizFilter === "all" ||
                (lessonQuizFilter === "with_quiz" && hasQuiz) ||
                (lessonQuizFilter === "no_quiz" && !hasQuiz);

            const matchesCanonical =
                lessonCanonicalFilter === "all" ||
                (lessonCanonicalFilter === "with_canonical" && hasCanonical) ||
                (lessonCanonicalFilter === "no_canonical" && !hasCanonical);

            const matchesLessonType =
                lessonTypeFilter === "all" ||
                (lesson.lesson_type || "").trim() === lessonTypeFilter;

            return matchesApproval && matchesQuiz && matchesCanonical && matchesLessonType;
        };

        const next: Record<number, { level: Level | null, modules: Module[] }> = {};
        for (const [key, group] of Object.entries(modulesByLevel)) {
            const levelId = Number(key);
            const filteredModules = group.modules
                .map((module) => ({
                    ...module,
                    lessons: module.lessons.filter(lessonMatchesFilters),
                }))
                .filter((module) => module.lessons.length > 0);

            if (filteredModules.length > 0) {
                next[levelId] = { level: group.level, modules: filteredModules };
            }
        }
        return next;
    })();

    // Sort levels: defined levels by level_number, then "General" (null) at the end (or beginning, depending on preference. Let's put General at the end if it implies "intro" or "extra", or rely on course.levels order)
    // Actually, we can iterate through `course.levels` to ensure order, and pick up any leftovers.

    const sortedLevelIds = Object.keys(filteredModulesByLevel).map(Number).sort((a, b) => {
        if (a === -1) return 1; // Put General at the end
        if (b === -1) return -1;
        const levelA = filteredModulesByLevel[a].level;
        const levelB = filteredModulesByLevel[b].level;
        return (levelA?.level_number || 0) - (levelB?.level_number || 0);
    });
    const filteredLessonsCount = Object.values(filteredModulesByLevel).reduce(
        (acc, group) => acc + group.modules.reduce((mAcc, module) => mAcc + module.lessons.length, 0),
        0,
    );

    return (
        <div className="w-full space-y-8">
            <header className="sticky top-0 z-20 -mx-4 border-b border-gray-200 bg-white shadow-md sm:-mx-6 md:-mx-8">
                <div className="w-full px-4 py-5 sm:px-6 sm:py-6 md:px-8">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0 flex-1">
                            <div className="mb-1 flex flex-wrap items-center gap-2 gap-y-1">
                                <button
                                    type="button"
                                    onClick={() => navigate("/admin/courses")}
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                                    aria-label="Back to courses"
                                    title="Back to courses"
                                >
                                    <ArrowLeft className="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setActiveView("lessons")}
                                    className="text-left text-2xl font-extrabold tracking-tight text-gray-900 transition-colors hover:text-slate-700 sm:text-3xl"
                                    title="Go to course lesson list"
                                >
                                    {course.title}
                                </button>
                                {(() => {
                                    const status = course.status?.toLowerCase() || "draft";
                                    const color =
                                        status === "published"
                                            ? "text-emerald-600 border-emerald-200 bg-emerald-50"
                                            : "text-slate-500 border-slate-200 bg-slate-50";
                                    return (
                                        <span
                                            className={`rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${color}`}
                                        >
                                            {status}
                                        </span>
                                    );
                                })()}
                                <span
                                    className={`rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                                        isCourseSponsored
                                            ? "text-violet-700 border-violet-200 bg-violet-50"
                                            : "text-slate-600 border-slate-200 bg-slate-50"
                                    }`}
                                >
                                    {isCourseSponsored ? "Sponsored" : "Individual"}
                                </span>
                            </div>
                            <p className="text-sm text-gray-500">
                                {totalModules} modules • {totalLessons} lessons
                            </p>
                        </div>

                        <div className="flex flex-shrink-0 flex-wrap items-center gap-3">
                    {(() => {
                        const isPublished = course.status === "PUBLISHED";
                        const lessons = modules.flatMap(m => m.lessons);
                        const hasCoverImage = Boolean(course.cover_image_url);
                        const allCanonicalLessonsApproved =
                            lessons.length > 0 && lessons.every((lesson) => getLessonStatus(lesson) === "APPROVED");
                        const allLessonsHaveQuizzesGenerated =
                            lessons.length > 0 &&
                            lessons.every((lesson) => {
                                const raw = lesson.generated_lessons;
                                const gls = Array.isArray(raw) ? raw : raw ? [raw] : [];
                                return gls.some((gl) => gl.quiz_generated === true);
                            });
                        const placementQuizGenerated =
                            placementQuizzes.length > 0 &&
                            placementQuizzes.every((quiz) => quiz.status === "APPROVED");

                        const unmetPublishConditions: string[] = [];
                        if (!allCanonicalLessonsApproved) {
                            unmetPublishConditions.push("all canonical lessons approved");
                        }
                        if (!allLessonsHaveQuizzesGenerated && !blockOnlyCourse) {
                            unmetPublishConditions.push("all lessons have quizzes generated");
                        }
                        if (!hasCoverImage) {
                            unmetPublishConditions.push("cover image present");
                        }
                        if (!placementQuizGenerated) {
                            unmetPublishConditions.push("placement quiz approved");
                        }

                        // Can always move TO draft. Can only move TO published if all is approved.
                        const publishDisabled =
                            !isPublished && unmetPublishConditions.length > 0;
                        const tooltipMessage = publishDisabled
                            ? `Before publishing: ${unmetPublishConditions.join(", ")}.`
                            : "";

                        return (
                            <TooltipProvider>
                                <Tooltip delayDuration={0}>
                                    <TooltipTrigger asChild>
                                        <button
                                            onClick={() => !publishDisabled && handleUpdateStatus(isPublished ? "DRAFT" : "PUBLISHED")}
                                            disabled={publishDisabled}
                                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all border ${isPublished
                                                ? "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                                                : publishDisabled
                                                    ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                                                    : "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700 shadow-sm"
                                                }`}
                                        >
                                            {isPublished ? "Move to Draft" : "Publish Course"}
                                        </button>
                                    </TooltipTrigger>
                                    {publishDisabled && (
                                        <TooltipContent side="top" className="bg-slate-900 text-white border-slate-800 py-2 px-3 text-sm font-medium shadow-xl">
                                            {tooltipMessage}
                                        </TooltipContent>
                                    )}
                                </Tooltip>
                            </TooltipProvider>
                        );
                    })()}

                    <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white p-1">
                        <button
                            type="button"
                            onClick={() => setActiveView("lessons")}
                            className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${activeView === "lessons"
                                ? "bg-slate-100 text-slate-900"
                                : "text-slate-600 hover:text-slate-900"
                                }`}
                        >
                            Lessons
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setActiveView("placement");
                                const params = new URLSearchParams(searchParams.toString());
                                params.set("view", "placement");
                                routerReplace(`${pathname}?${params.toString()}`);
                            }}
                            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${activeView === "placement"
                                ? "bg-violet-100 text-violet-900"
                                : "text-slate-600 hover:text-slate-900"
                                }`}
                        >
                            Placement
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveView("prompts")}
                            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${activeView === "prompts"
                                ? "bg-amber-100 text-amber-900"
                                : "text-slate-600 hover:text-slate-900"
                                }`}
                        >
                            <FileText className="h-4 w-4" />
                            Prompts
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveView("metadata")}
                            className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${activeView === "metadata"
                                ? "bg-indigo-100 text-indigo-900"
                                : "text-slate-600 hover:text-slate-900"
                                }`}
                        >
                            Course metadata
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveView("resources")}
                            className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${activeView === "resources"
                                ? "bg-emerald-100 text-emerald-900"
                                : "text-slate-600 hover:text-slate-900"
                                }`}
                        >
                            Resources
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveView("pricing")}
                            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${activeView === "pricing"
                                ? "bg-teal-100 text-teal-900"
                                : "text-slate-600 hover:text-slate-900"
                                }`}
                        >
                            <DollarSign className="h-4 w-4" />
                            Pricing
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveView("translations")}
                            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${activeView === "translations"
                                ? "bg-sky-100 text-sky-900"
                                : "text-slate-600 hover:text-slate-900"
                                }`}
                        >
                            <Languages className="h-4 w-4" />
                            Translations
                        </button>
                    </div>
                    <MenuDropdown
                        label="Actions"
                        variant="secondary"
                        items={[
                            {
                                label: "Upload Lesson",
                                icon: Upload,
                                onClick: () => setIsUploadModalOpen(true)
                            },
                            {
                                label: "Approve All Lessons",
                                icon: SparklesIcon,
                                onClick: handleApproveAll
                            },
                            ...(canonicalStatus?.failed && canonicalStatus.failed > 0 ? [{
                                label: "Retry Failed Canonical Lessons",
                                icon: Loader2,
                                onClick: () => handleGenerateCanonicalForCourse(true),
                                disabled: isCanonicalFanoutSubmitting
                            }] : []),
                            {
                                label: "Manage Cover Image",
                                icon: ImageLucide,
                                onClick: () => {
                                    setCoverError(null);
                                    setCoverUrlDraft(course.cover_image_url ?? "");
                                    setIsCoverDialogOpen(true);
                                },
                            },
                            {
                                label: "Edit Course Details",
                                icon: Pencil,
                                onClick: openCourseDetailsDialog,
                            },
                            {
                                label: "Edit Canonical Draft",
                                icon: Pencil,
                                onClick: () => navigate(`/admin/courses/canonical-lessons?courseId=${course.id}`),
                            },
                            {
                                label: isCourseSponsored ? "Set as individual access" : "Set as sponsored",
                                icon: Building2,
                                onClick: () =>
                                    void handleSetAccessMode(isCourseSponsored ? "INDIVIDUAL" : "SPONSORED"),
                                disabled: accessModeUpdating || !hasCourseOrganization,
                            },
                            {
                                label: deleteInProgress ? "Deleting…" : "Delete course",
                                icon: Trash2,
                                onClick: () => void handleDeleteCourse(),
                                disabled: deleteInProgress,
                                className: "text-red-700",
                            },
                        ]}
                    />
                        </div>

                    </div>
                </div>
            </header>

            <div className="w-full">
            {activeView === "lessons" ? (
                <LessonsTab
                    canonicalStatus={canonicalStatus}
                    canonicalProgressTotal={canonicalProgressTotal}
                    canonicalProgressGenerated={canonicalProgressGenerated}
                    canonicalProgressPercent={canonicalProgressPercent}
                    isCanonicalFanoutSubmitting={isCanonicalFanoutSubmitting}
                    handleGenerateCanonicalForCourse={handleGenerateCanonicalForCourse}
                    hasActiveLessonFilters={hasActiveLessonFilters}
                    filteredLessonsCount={filteredLessonsCount}
                    totalLessons={totalLessons}
                    lessonApprovalFilter={lessonApprovalFilter}
                    setLessonApprovalFilter={setLessonApprovalFilter}
                    lessonQuizFilter={lessonQuizFilter}
                    setLessonQuizFilter={setLessonQuizFilter}
                    lessonCanonicalFilter={lessonCanonicalFilter}
                    setLessonCanonicalFilter={setLessonCanonicalFilter}
                    lessonTypeFilter={lessonTypeFilter}
                    setLessonTypeFilter={setLessonTypeFilter}
                    lessonTypeOptions={lessonTypeOptions}
                    placementQuizzes={placementQuizzes}
                    courseId={course.id}
                    onOpenPlacementTab={openPlacementTab}
                    sortedLevelIds={sortedLevelIds}
                    filteredModulesByLevel={filteredModulesByLevel}
                    expandedModules={expandedModules}
                    toggleModule={toggleModule}
                    getStatusParams={getStatusParams}
                    getLessonStatus={getLessonStatus}
                    getLessonQuizBadge={getLessonQuizBadge}
                    canonicalGeneratingByLessonId={canonicalGeneratingByLessonId}
                    generatingCanonicalLessonId={generatingCanonicalLessonId}
                    handleGenerateCanonical={handleGenerateCanonical}
                    handleViewLesson={handleViewLesson}
                    resetLessonFilters={resetLessonFilters}
                />
            ) : activeView === "placement" ? (
                <PlacementTab
                    courseId={course.id}
                    courseUuid={course.uuid ?? id}
                    courseLevels={course.levels ?? []}
                    generatePlacementQuizDisabledReason={placementQuizGenerateDisabledReason}
                    onGeneratePlacementQuiz={handleGenerateQuiz}
                    reloadNonce={placementReloadNonce}
                />
            ) : activeView === "prompts" ? (
                <PromptsTab
                    course={course}
                    onPromptSaved={(key, value) => {
                        setCourse((prev) => (prev ? { ...prev, [key]: value } : prev));
                    }}
                />
            ) : activeView === "pricing" ? (
                <PlansTab
                    courseId={course.id}
                    courseTitle={course.title}
                    accessMode={courseAccessMode}
                    onToast={showToast}
                />
            ) : activeView === "resources" ? (
                course && (
                    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
                        <div className="border-b border-slate-100 bg-slate-50/60 px-6 py-4">
                            <h2 className="text-lg font-bold text-slate-900">Resources</h2>
                            <p className="text-sm text-slate-500">
                                Supplementary materials for students enrolled in this course.
                            </p>
                        </div>
                        <div className="px-6 py-5">
                            {loadingResources ? (
                                <div className="space-y-2">
                                    {[...Array(3)].map((_, i) => (
                                        <div key={i} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 animate-pulse">
                                            <div className="h-9 w-9 flex-shrink-0 rounded-lg bg-slate-100" />
                                            <div className="flex-1 space-y-2">
                                                <div className="h-3 w-1/3 rounded bg-slate-100" />
                                                <div className="h-2.5 w-1/2 rounded bg-slate-100" />
                                            </div>
                                            <div className="flex gap-1.5">
                                                <div className="h-7 w-14 rounded-md bg-slate-100" />
                                                <div className="h-7 w-7 rounded-md bg-slate-100" />
                                                <div className="h-7 w-7 rounded-md bg-slate-100" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <ResourceList
                                    resources={resources}
                                    courseId={course.id}
                                    modules={course.modules.map(m => ({ id: m.id, title: m.title, levelName: m.levels?.name ?? null, lessons: m.lessons.map(l => ({ id: l.id, title: l.title })) }))}
                                    onResourceCreated={handleResourceCreated}
                                    onResourceUpdated={handleResourceUpdated}
                                    onResourceDeleted={handleResourceDeleted}
                                />
                            )}
                        </div>
                    </div>
                )
            ) : activeView === "translations" ? (
                <TranslationsTab
                    statuses={translationStatuses}
                    loadError={translationsLoadError}
                    onTranslate={handleTranslate}
                    onSetOffered={handleSetOffered}
                    onSyncStale={handleSyncStale}
                    onRefresh={fetchTranslationStatus}
                    busy={translationBusy}
                    refreshing={loadingTranslations}
                />
            ) : (
                <MetadataTab
                    domains={domains}
                    goals={goals}
                    departments={departments}
                    editingId={editingId}
                    editingLabel={editingLabel}
                    setEditingId={setEditingId}
                    setEditingLabel={setEditingLabel}
                    metadataBusyKey={metadataBusyKey}
                    newDomainLabel={newDomainLabel}
                    setNewDomainLabel={setNewDomainLabel}
                    newGoalLabel={newGoalLabel}
                    setNewGoalLabel={setNewGoalLabel}
                    newDepartmentLabel={newDepartmentLabel}
                    setNewDepartmentLabel={setNewDepartmentLabel}
                    handleDeleteMetadata={handleDeleteMetadata}
                    handleSaveMetadataLabel={handleSaveMetadataLabel}
                    handleAddMetadata={handleAddMetadata}
                    requireUserFutureGoal={course.require_user_future_goal !== false}
                    onChangeRequireUserFutureGoal={handleToggleRequireUserFutureGoal}
                    preferencePromptSaving={preferencePromptSaving}
                />
            )}

            <UploadLessonModal
                isOpen={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
                onSuccess={() => {
                    setIsUploadModalOpen(false);
                    fetchCourse(); // Refresh data in background instead of full page reload
                }}
            />

            <TaskProgressModal
                isOpen={taskModal.isOpen}
                onClose={() => setTaskModal(prev => ({ ...prev, isOpen: false }))}
                status={taskModal.status}
                title={
                    taskModal.title || (
                        taskModal.status === "processing" ? "Starting Generation..." : taskModal.status === "completed" ? "Generation Complete" : undefined
                    )
                }
                description={
                    taskModal.description || (
                        taskModal.status === "processing"
                            ? "We are queueing the placement quiz generation task. This will run in the background."
                            : taskModal.status === "completed"
                                ? "The placement quiz has been generated successfully."
                                : undefined
                    )
                }
                error={taskModal.error}
            />

            {toast ? (
                <div
                    role="status"
                    className={`fixed bottom-4 right-4 z-[60] max-w-sm rounded-md px-4 py-3 text-sm font-medium text-white shadow-lg transition-opacity whitespace-pre-wrap ${toast.type === "success" ? "bg-emerald-600" : "bg-red-600"}`}
                >
                    {toast.message}
                </div>
            ) : null}

            <Transition appear show={isCoverDialogOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setIsCoverDialogOpen(false)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/30 backdrop-blur-[1px]" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-200"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-150"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-2xl rounded-xl border border-gray-200 bg-white shadow-xl">
                                    <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4">
                                        <div className="min-w-0">
                                            <Dialog.Title className="text-lg font-semibold text-gray-900">
                                                Manage course cover image
                                            </Dialog.Title>
                                            <p className="mt-1 text-sm text-gray-500">
                                                This image is shown to students on the course list. JPG, PNG, WebP, or GIF · max 5 MB.
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setIsCoverDialogOpen(false)}
                                            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                            aria-label="Close cover image dialog"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>

                                    <div className="p-6 flex flex-col sm:flex-row gap-5 sm:items-center">
                                        <div className="relative w-full sm:w-56 shrink-0 aspect-[4/3] rounded-xl overflow-hidden bg-gray-100 ring-1 ring-gray-200/80 shadow-inner">
                                            {course.cover_image_url ? (
                                                <Image
                                                    src={course.cover_image_url}
                                                    alt=""
                                                    fill
                                                    className="object-cover"
                                                    unoptimized
                                                />
                                            ) : (
                                                <div className="absolute inset-0 flex items-center justify-center p-4 bg-gradient-to-br from-gray-50 via-white to-gray-50">
                                                    <div className="text-center w-full">
                                                        <div className="mx-auto w-12 h-12 rounded-2xl bg-white border-2 border-dashed border-gray-300 flex items-center justify-center shadow-sm">
                                                            <Upload className="h-5 w-5 text-gray-600" aria-hidden />
                                                        </div>
                                                        <p className="mt-3 text-sm font-semibold text-gray-900">Upload a cover image</p>
                                                        <p className="mt-1 text-xs text-gray-500">Required to publish the course</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex flex-col gap-3 min-w-0">
                                            <input
                                                ref={coverInputRef}
                                                type="file"
                                                accept="image/jpeg,image/png,image/webp,image/gif"
                                                className="hidden"
                                                onChange={handleCoverImageSelected}
                                                disabled={coverUploading}
                                            />
                                            <div className="flex flex-wrap items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => coverInputRef.current?.click()}
                                                    disabled={coverUploading}
                                                    className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold bg-white text-gray-800 border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                                                >
                                                    {coverUploading
                                                        ? "Uploading…"
                                                        : course.cover_image_url
                                                          ? "Replace image"
                                                          : "Select image"}
                                                </button>
                                                {course.cover_image_url ? (
                                                    <button
                                                        type="button"
                                                        onClick={handleRemoveCoverImage}
                                                        disabled={coverUploading}
                                                        className="text-sm font-medium text-gray-500 hover:text-gray-800 underline-offset-2 hover:underline disabled:opacity-50"
                                                    >
                                                        Remove image
                                                    </button>
                                                ) : null}
                                            </div>
                                            <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                                                <label htmlFor="cover-image-url" className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                    Or paste image URL
                                                </label>
                                                <div className="flex flex-col gap-2 sm:flex-row">
                                                    <input
                                                        id="cover-image-url"
                                                        type="url"
                                                        value={coverUrlDraft}
                                                        onChange={(e) => setCoverUrlDraft(e.target.value)}
                                                        disabled={coverUploading}
                                                        placeholder="https://example.com/cover.jpg"
                                                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:opacity-60"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={handleSaveCoverImageUrl}
                                                        disabled={coverUploading}
                                                        className="inline-flex items-center justify-center rounded-lg border border-indigo-600 bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:opacity-50"
                                                    >
                                                        Save URL
                                                    </button>
                                                </div>
                                            </div>
                                            {coverError ? (
                                                <p className="text-sm text-red-600" role="alert">
                                                    {coverError}
                                                </p>
                                            ) : null}
                                        </div>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            <Transition appear show={isCourseDetailsDialogOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => !courseDetailsSaving && setIsCourseDetailsDialogOpen(false)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/30 backdrop-blur-[1px]" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-200"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-150"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-2xl rounded-xl border border-gray-200 bg-white shadow-xl">
                                    <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4">
                                        <div className="min-w-0">
                                            <Dialog.Title className="text-lg font-semibold text-gray-900">
                                                Edit course details
                                            </Dialog.Title>
                                            <p className="mt-1 text-sm text-gray-500">
                                                Update the course name and description shown to students.
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setIsCourseDetailsDialogOpen(false)}
                                            disabled={courseDetailsSaving}
                                            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                                            aria-label="Close course details dialog"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                    <div className="space-y-4 p-6">
                                        <div>
                                            <label htmlFor="course-title" className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                Course name
                                            </label>
                                            <input
                                                id="course-title"
                                                type="text"
                                                value={courseTitleDraft}
                                                onChange={(e) => setCourseTitleDraft(e.target.value)}
                                                disabled={courseDetailsSaving}
                                                className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-medium text-slate-900 shadow-sm outline-none transition hover:border-slate-300 hover:bg-white focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-100 disabled:opacity-60"
                                                placeholder="Enter course name"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="course-description" className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                Course description
                                            </label>
                                            <textarea
                                                id="course-description"
                                                value={courseDescriptionDraft}
                                                onChange={(e) => setCourseDescriptionDraft(e.target.value)}
                                                disabled={courseDetailsSaving}
                                                rows={4}
                                                className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition hover:border-slate-300 hover:bg-white focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-100 disabled:opacity-60"
                                                placeholder="Add a short description for students"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="course-schema-version" className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                Course type
                                            </label>
                                            <select
                                                id="course-schema-version"
                                                value={courseSchemaVersionDraft}
                                                onChange={(e) => setCourseSchemaVersionDraft(e.target.value as "engineering" | "professional")}
                                                disabled={courseDetailsSaving}
                                                className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-medium text-slate-900 shadow-sm outline-none transition hover:border-slate-300 hover:bg-white focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-100 disabled:opacity-60"
                                            >
                                                <option value="engineering">AI for Engineers</option>
                                                <option value="professional">AI for Professionals</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                Course Visibility
                                            </label>
                                            <div className="mt-2 grid grid-cols-2 gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setCourseIsPublicDraft(true)}
                                                    disabled={courseDetailsSaving}
                                                    className={`flex items-center justify-center gap-2 rounded-xl border p-2.5 text-xs font-semibold transition ${
                                                        courseIsPublicDraft
                                                            ? "border-indigo-600 bg-indigo-50/70 text-indigo-700 shadow-sm"
                                                            : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                                                    }`}
                                                >
                                                    <Globe className="h-4 w-4 shrink-0 text-indigo-600" />
                                                    <span>Public (All Orgs)</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setCourseIsPublicDraft(false)}
                                                    disabled={courseDetailsSaving}
                                                    className={`flex items-center justify-center gap-2 rounded-xl border p-2.5 text-xs font-semibold transition ${
                                                        !courseIsPublicDraft
                                                            ? "border-indigo-600 bg-indigo-50/70 text-indigo-700 shadow-sm"
                                                            : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                                                    }`}
                                                >
                                                    <Lock className="h-4 w-4 shrink-0 text-amber-600" />
                                                    <span>Restricted</span>
                                                </button>
                                            </div>
                                            <p className="mt-1 text-[11px] text-slate-500">
                                                {courseIsPublicDraft
                                                    ? "Visible to all B2B organizations for workspace assignment, and public learners."
                                                    : "Only visible and assignable to the specific organizations selected below."}
                                            </p>
                                        </div>

                                        {!courseIsPublicDraft && (
                                            <div>
                                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                    Authorized Organizations ({courseOrgsDraft.length} selected)
                                                </label>
                                                {orgsLoadError ? (
                                                    <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">Failed to load organizations.</p>
                                                ) : (
                                                    <div className="mt-2 max-h-48 overflow-y-auto rounded-xl border border-slate-200 p-2 space-y-1 bg-slate-50/50">
                                                        {organizations.length === 0 ? (
                                                            <p className="text-xs text-slate-400 p-1">No organizations found.</p>
                                                        ) : (
                                                            organizations.map((org) => {
                                                                const isSelected = courseOrgsDraft.includes(org.id);
                                                                return (
                                                                    <button
                                                                        key={org.id}
                                                                        type="button"
                                                                        disabled={courseDetailsSaving}
                                                                        onClick={() => {
                                                                            setCourseOrgsDraft((prev) =>
                                                                                isSelected ? prev.filter((id) => id !== org.id) : [...prev, org.id]
                                                                            );
                                                                        }}
                                                                        className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-xs font-medium transition ${
                                                                            isSelected
                                                                                ? "bg-indigo-100 text-indigo-900"
                                                                                : "text-slate-700 hover:bg-slate-100"
                                                                        }`}
                                                                    >
                                                                        <span className="truncate">{org.name}</span>
                                                                        {isSelected && <Check className="h-3.5 w-3.5 text-indigo-600 shrink-0" />}
                                                                    </button>
                                                                );
                                                            })
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {courseDetailsError ? (
                                            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{courseDetailsError}</p>
                                        ) : null}
                                        <div className="flex justify-end gap-2 pt-2">
                                            <button
                                                type="button"
                                                onClick={() => setIsCourseDetailsDialogOpen(false)}
                                                disabled={courseDetailsSaving}
                                                className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleSaveCourseDetails}
                                                disabled={courseDetailsSaving}
                                                className="inline-flex items-center justify-center rounded-lg border border-indigo-600 bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:opacity-50"
                                            >
                                                {courseDetailsSaving ? "Saving..." : "Save changes"}
                                            </button>
                                        </div>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
            </div>
        </div>
    );
}
